import { COUNTRIES, EMPTY_ADDRESS_FORM, STATES } from '../../../constants';
import { Employment } from '../../../types';
import {
  capitalizeEveryWord,
  formatFormAddressForApi,
  hasAddressContent,
  stripLocationSuffix,
  transformCodeToSelectOption,
} from '../../utils';
import { EmploymentFormValues } from './types';

export const employmentFormToApi = (formEmployment: EmploymentFormValues) => {
  if (formEmployment.endDate === '') {
    delete formEmployment.endDate;
  }
  const facilityValue = formEmployment.facility.value;
  const hasLinkedFacility = Boolean(facilityValue && facilityValue !== 'userAdded');
  const facilityAddress = formEmployment.facility.address;
  const facilityAddressFilled = hasAddressContent(facilityAddress);
  const shouldSendFacility =
    !!formEmployment.facility.userAdded || (hasLinkedFacility && facilityAddressFilled);
  return JSON.stringify({
    ...formEmployment,
    apptType: formEmployment.apptType.label,
    endDate: formEmployment.endDate ? formEmployment.endDate : null,
    facilityId: hasLinkedFacility ? facilityValue : null,
    facility: shouldSendFacility
      ? {
          userAddedFlag: !!formEmployment.facility.userAdded,
          id: hasLinkedFacility ? facilityValue : undefined,
          // [MCV-1876] Never persist the picker's "(City, ST)" label suffix as the name.
          // A blank address still carries the default state, which is not saved.
          name: facilityAddressFilled
            ? stripLocationSuffix(
                formEmployment.facility.label,
                facilityAddress?.city,
                facilityAddress?.stateCode?.value
              )
            : formEmployment.facility.label,
          address: formatFormAddressForApi(
            facilityAddressFilled
              ? { ...EMPTY_ADDRESS_FORM, ...facilityAddress }
              : {
                  ...EMPTY_ADDRESS_FORM,
                  ...facilityAddress,
                  stateCode: { value: '', label: '' },
                  countryCode: { value: '', label: '' },
                }
          ),
        }
      : null,
    privsHeldFlag: formEmployment.privilegesHeldCheckbox,
    rehireEligibleFlag: formEmployment.rehireToggle,
  } as Employment);
};

export const employmentApiToForm = (employmentStr: string): EmploymentFormValues => {
  const employment: Employment = JSON.parse(employmentStr);
  const facilityValue =
    employment.facility?.id?.toString() ?? employment.facilityId?.toString() ?? '';
  const facilityLabel = capitalizeEveryWord(employment.facility?.name || employment.employer || '');
  // MCV-1412: AI parse may produce employer-only records (facility null, no id)
  // when the backend's facility matcher doesn't find a row. The displayable label
  // already falls back to employer above; mirror that here so userAdded is set
  // and the wizard's Next guard passes.
  const isUnpersistedFacilityName = Boolean(facilityLabel && !facilityValue);

  // AI-parsed records may return null for fields the form requires.
  // Explicit guards override spread values for at-risk fields.
  return {
    ...employment,
    employer: employment.employer ?? '',
    startDate: employment.startDate ?? '',
    title: employment.title ?? '',
    department: employment.department ?? '',
    leaveReason: employment.leaveReason ?? '',
    rehireEligibleComment: employment.rehireEligibleComment ?? '',
    apptType: employment.apptType
      ? { label: employment.apptType, value: employment.apptType }
      : { label: '', value: '' },
    currentFlag: employment.endDate == undefined,
    privilegesHeldCheckbox: employment.privsHeldFlag ?? false,
    rehireToggle: employment.rehireEligibleFlag ?? false,
    validFlag: employment.validFlag ?? true,
    warnings: employment.warnings ?? {},
    facility: employment.facility?.address
      ? {
          value: facilityValue,
          label: facilityLabel,
          userAdded: !!employment.facility.userAddedFlag || isUnpersistedFacilityName,
          address: {
            ...employment.facility.address,
            addressType: undefined,
            countryCode: transformCodeToSelectOption(
              employment.facility?.address.countryCode,
              COUNTRIES,
              { value: '', label: '' }
            ),
            stateCode: transformCodeToSelectOption(employment.facility.address.stateCode, STATES, {
              value: '',
              label: '',
            }),
          },
        }
      : {
          value: employment.facility?.id?.toString() ?? '',
          label: capitalizeEveryWord(employment.facility?.name || employment.employer || ''),
          userAdded: !!employment.facility?.userAddedFlag || isUnpersistedFacilityName,
          address: EMPTY_ADDRESS_FORM,
        },
  };
};

export const employmentsApiToForm = (employmentsStr: string): EmploymentFormValues[] => {
  const employments: Employment[] = JSON.parse(employmentsStr);
  return employments.map((employment) => employmentApiToForm(JSON.stringify(employment)));
};
